'use client';

import { Fragment, useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import type { Category, CategoryBreadcrumbSegment, SearchPayload } from '@/lib/api';
import { ProductCard } from './ProductCard';
import { ClassifiedAdCard } from './ClassifiedAdCard';
import type { SearchFilters } from './FilterPanel';
import type { ClassifiedAd } from './ClassifiedAdCard';
import { Home, LayoutGrid, Filter } from 'lucide-react';
import { getCategoryIcon } from '@/utils/categoryIcons';
import { buildListingHref, searchPathWithVerifiedOnly } from '@/lib/searchRoutes';
import { readVerifiedOnlySession, writeVerifiedOnlySession } from '@/lib/verifiedOnlySession';
import {
  parseBrandsFromSearchParams,
  parseConditionsFromSearchParams,
  parseFuelTypeFromSearchParams,
  parseListingTypeFromSearchParams,
  parseMinRatingFromSearchParams,
  parseTransmissionFromSearchParams,
} from '@/lib/listingSearchParams';
import { ThirdPartyAd } from './ThirdPartyAd';
import { getCategoryListingSeo, SEARCH_INDEX_SEO } from '@/lib/categorySeo';

const FilterPanel = dynamic(
  () => import('./FilterPanel').then((mod) => mod.FilterPanel),
  { loading: () => null },
);

interface SearchPageProps {
  data: SearchPayload;
  searchParams: {
    q?: string;
    category?: string;
    page?: string;
    min_price?: string;
    max_price?: string;
    location?: string;
    verified_only?: string;
    sort?: string;
    radius?: string;
    min_rating?: string;
    listing_type?: string;
    vehicle_brand?: string;
    vehicle_model?: string;
    year_min?: string;
    year_max?: string;
    mileage_min?: string;
    mileage_max?: string;
    engine_cc_min?: string;
    engine_cc_max?: string;
    'conditions[]'?: string | string[];
    'brands[]'?: string | string[];
    'fuel_type[]'?: string | string[];
    'transmission[]'?: string | string[];
  };
}

function getProductList(payload: SearchPayload): {
  list: import('@/lib/api').ProductSummary[];
  currentPage?: number;
  lastPage?: number;
  total?: number;
} {
  const products = payload.products;
  if (Array.isArray(products)) return { list: products };
  if (products && typeof products === 'object' && 'data' in products) {
    const p = products as import('@/lib/api').Paginated<import('@/lib/api').ProductSummary>;
    return { list: p.data ?? [], currentPage: p.current_page, lastPage: p.last_page, total: p.total };
  }
  return { list: [] };
}

function getAdsList(payload: SearchPayload): {
  list: ClassifiedAd[];
  currentPage?: number;
  lastPage?: number;
  total?: number;
} {
  const ads = payload.classifiedAds;
  if (!ads) return { list: [] };
  if (Array.isArray(ads)) return { list: ads as ClassifiedAd[] };
  if (typeof ads === 'object' && 'data' in ads) {
    const a = ads as import('@/lib/api').Paginated<ClassifiedAd>;
    return { list: a.data ?? [], currentPage: a.current_page, lastPage: a.last_page, total: a.total };
  }
  return { list: [] };
}

/** When API sends resolved category tree IDs, drop stray rows (defense in depth). */
function filterRowsByCategoryIds<T>(
  rows: T[],
  ids: number[] | null | undefined,
): T[] {
  if (ids == null) return rows;
  if (ids.length === 0) return [];
  const allowed = new Set(ids);
  const getRowCategoryId = (row: unknown): number | null => {
    if (!row || typeof row !== 'object') return null;
    const anyRow = row as Record<string, unknown>;
    const direct = anyRow.categoryId ?? anyRow.category_id;
    if (direct != null) {
      const n = Number(direct);
      return Number.isFinite(n) ? n : null;
    }
    const cat = anyRow.category;
    if (cat && typeof cat === 'object' && 'id' in (cat as object)) {
      const n = Number((cat as { id?: unknown }).id);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  };
  return rows.filter((row) => {
    const id = getRowCategoryId(row);
    // If API row does not expose a usable category id, keep it instead of dropping all results.
    // Server-side query already applies the category filter and is the source of truth.
    if (id == null) return true;
    return allowed.has(id);
  });
}

/** Last segment of hierarchical slugs (parent--child) for a readable title. */
function humanizeCategorySlug(slug: string): string {
  const segment = slug.includes('--') ? (slug.split('--').pop() ?? slug) : slug;
  const words = segment.split('-').filter(Boolean);
  const title = words.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
  if (/free stuff/i.test(title)) return 'Free Stuff';
  return title;
}

function resolveActiveCategory(
  categories: Category[],
  slug: string | undefined,
  apiCategoryId: number | undefined,
): Category | undefined {
  if (slug == null || String(slug).trim() === '') return undefined;
  const s = String(slug).trim();
  const fromList = categories.find((c) => String(c.id) === s || c.slug === s);
  if (fromList) return fromList;
  const id =
    apiCategoryId != null && Number.isFinite(apiCategoryId) && apiCategoryId > 0 ? apiCategoryId : 0;
  return {
    id,
    name: humanizeCategorySlug(s),
    slug: s,
    icon: null,
  };
}

export function SearchPage({ data, searchParams }: SearchPageProps) {
  const router = useRouter();
  const pathname = usePathname();
  const categoryFilterIds = (() => {
    const raw = data.filters?.categoryFilterIds;
    if (raw == null) return null;
    if (!Array.isArray(raw)) return null;
    return raw.map((x) => Number(x)).filter((n) => Number.isFinite(n));
  })();

  const hasCategoryScope =
    searchParams.category != null && String(searchParams.category).trim() !== '';

  const { list: productsRaw, currentPage: pCurrent = 1, lastPage: pLast = 1, total: pTotal } = getProductList(data);
  const { list: adsRaw, currentPage: aCurrent = 1, lastPage: aLast = 1, total: aTotal } = getAdsList(data);
  const products = hasCategoryScope
    ? productsRaw
    : filterRowsByCategoryIds(productsRaw, categoryFilterIds);
  const ads = hasCategoryScope ? adsRaw : filterRowsByCategoryIds(adsRaw, categoryFilterIds);
  const currentPage = Math.max(pCurrent, aCurrent);
  const lastPage = Math.max(pLast, aLast);
  const categories = data.categories ?? [];
  const categoryBreadcrumb: CategoryBreadcrumbSegment[] = Array.isArray(data.categoryBreadcrumb)
    ? data.categoryBreadcrumb
    : [];
  const categoryTypeOptions = data.categoryTypeOptions ?? [];
  const categoryTypeLabel = data.categoryTypeLabel ?? null;
  const isVehicleCategory = Boolean(data.isVehicleCategory);
  const resolvedFiltersCategoryId =
    typeof data.filters?.categoryId === 'number' && Number.isFinite(data.filters.categoryId)
      ? data.filters.categoryId
      : undefined;
  const activeCategory = resolveActiveCategory(
    categories,
    searchParams.category,
    resolvedFiltersCategoryId,
  );
  // SEO page heading (the audit found this <h1> "Missing"). Prefer the hand-tuned per-category
  // value; fall back to the category name, or the all-listings heading on the bare /search page.
  const listingSeo = getCategoryListingSeo(searchParams.category);
  const headingText =
    listingSeo?.h1 ?? (hasCategoryScope ? activeCategory?.name ?? 'Listings' : SEARCH_INDEX_SEO.h1);
  const totalListings = (pTotal ?? products.length) + (aTotal ?? ads.length);
  const combinedItems = [
    ...products.map((p) => ({ ...p, _type: 'product' as const })),
    ...ads.map((a) => ({ ...a, _type: 'ad' as const })),
  ];
  const adCategoryId =
    resolvedFiltersCategoryId != null && resolvedFiltersCategoryId > 0
      ? resolvedFiltersCategoryId
      : activeCategory && activeCategory.id > 0
        ? activeCategory.id
        : undefined;
  const topAdPlacement = hasCategoryScope ? 'category' : 'homepage';

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  const conditions = parseConditionsFromSearchParams(searchParams);
  const brands = parseBrandsFromSearchParams(searchParams);
  const minRating = parseMinRatingFromSearchParams(searchParams);
  const listingType = parseListingTypeFromSearchParams(searchParams.listing_type);
  const fuelType = parseFuelTypeFromSearchParams(searchParams);
  const transmission = parseTransmissionFromSearchParams(searchParams);
  const categoryForPath = activeCategory?.slug ?? searchParams.category;

  // Keep verified-only preference across category/search navigation within the session.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('verified_only')) {
      const v = params.get('verified_only') === '1' ? '1' : '0';
      writeVerifiedOnlySession(v);
      return;
    }
    const session = readVerifiedOnlySession();
    if (session !== '1') return;
    const href = buildListingHref({
      q: searchParams.q,
      category: categoryForPath,
      page: searchParams.page,
      min_price: searchParams.min_price,
      max_price: searchParams.max_price,
      location: searchParams.location,
      verified_only: session,
      sort: searchParams.sort,
      radius: searchParams.radius,
      conditions,
      brands,
      min_rating: minRating,
      listing_type: listingType,
    });
    router.replace(href);
  }, [
    pathname,
    searchParams.q,
    searchParams.page,
    searchParams.min_price,
    searchParams.max_price,
    searchParams.location,
    searchParams.sort,
    searchParams.radius,
    categoryForPath,
    conditions,
    brands,
    minRating,
    listingType,
    router,
  ]);

  const filters: SearchFilters = {
    q: searchParams.q,
    category: categoryForPath,
    page: searchParams.page,
    min_price: searchParams.min_price,
    max_price: searchParams.max_price,
    location: searchParams.location,
    verified_only: searchParams.verified_only,
    sort: searchParams.sort,
    radius: searchParams.radius,
    conditions,
    brands,
    min_rating: minRating,
    listing_type: listingType,
    vehicle_brand: searchParams.vehicle_brand,
    vehicle_model: searchParams.vehicle_model,
    year_min: searchParams.year_min,
    year_max: searchParams.year_max,
    mileage_min: searchParams.mileage_min,
    mileage_max: searchParams.mileage_max,
    engine_cc_min: searchParams.engine_cc_min,
    engine_cc_max: searchParams.engine_cc_max,
    fuel_type: fuelType,
    transmission,
  };

  const activeFilterCount =
    (filters.min_price ? 1 : 0) +
    (filters.max_price ? 1 : 0) +
    (filters.location ? 1 : 0) +
    (filters.verified_only === '1' ? 1 : 0) +
    (filters.vehicle_brand ? 1 : 0) +
    (filters.vehicle_model ? 1 : 0) +
    (filters.year_min || filters.year_max ? 1 : 0) +
    (filters.mileage_min || filters.mileage_max ? 1 : 0) +
    (filters.engine_cc_min || filters.engine_cc_max ? 1 : 0) +
    ((filters.fuel_type?.length ?? 0) > 0 ? 1 : 0) +
    ((filters.transmission?.length ?? 0) > 0 ? 1 : 0) +
    (filters.conditions?.length ?? 0) +
    (filters.min_rating ? 1 : 0) +
    (filters.listing_type ? 1 : 0) +
    (filters.brands?.length ?? 0);

  const buildSearchUrl = (page?: number) =>
    buildListingHref({
      q: searchParams.q,
      category: categoryForPath,
      page: page != null && page > 1 ? String(page) : undefined,
      min_price: searchParams.min_price,
      max_price: searchParams.max_price,
      location: searchParams.location,
      verified_only: searchParams.verified_only,
      sort: searchParams.sort,
      conditions,
      brands,
      min_rating: minRating,
      listing_type: listingType,
      vehicle_brand: searchParams.vehicle_brand,
      vehicle_model: searchParams.vehicle_model,
      year_min: searchParams.year_min,
      year_max: searchParams.year_max,
      mileage_min: searchParams.mileage_min,
      mileage_max: searchParams.mileage_max,
      engine_cc_min: searchParams.engine_cc_min,
      engine_cc_max: searchParams.engine_cc_max,
      fuel_type: fuelType,
      transmission,
    });

  /** Match classified ad details page: Sri Lanka by default, or the active location filter label. */
  const breadcrumbLocationLabel =
    typeof searchParams.location === 'string' && searchParams.location.trim() !== ''
      ? searchParams.location.trim()
      : 'Sri Lanka';
  const breadcrumbLocationHref =
    breadcrumbLocationLabel === 'Sri Lanka'
      ? '/'
      : buildListingHref({
          q: searchParams.q,
          category: categoryForPath,
          page: undefined,
          min_price: searchParams.min_price,
          max_price: searchParams.max_price,
          location: undefined,
          verified_only: searchParams.verified_only,
          conditions,
          brands,
        });

  return (
    <div className="mt-8">
      {/* Breadcrumb — same pattern as buyme-react ClassifiedAdDetails (icon / slashes / brand green) */}
      <div className="mb-6">
        {(categoryBreadcrumb.length > 0 || activeCategory) && (
          <nav
            className="flex flex-wrap items-center gap-x-1.5 gap-y-1 min-w-0 text-sm font-sans"
            aria-label="Breadcrumb"
          >
            <Link
              href="/"
              className="text-brand-green hover:text-brand-green/90 shrink-0 inline-flex items-center dark:text-brand-green"
              aria-label="Home"
            >
              <Home size={18} strokeWidth={2} className="text-brand-green" aria-hidden />
            </Link>
            <span className="text-neutral-400 select-none dark:text-neutral-500" aria-hidden>
              /
            </span>
            <Link
              href={breadcrumbLocationHref}
              className="text-brand-green font-medium hover:underline shrink-0 dark:text-brand-green"
            >
              {breadcrumbLocationLabel}
            </Link>
            {categoryBreadcrumb.length > 0
              ? categoryBreadcrumb.map((seg, idx) => {
                  const isLast = idx === categoryBreadcrumb.length - 1;
                  const href = searchPathWithVerifiedOnly(
                    `/search/${encodeURIComponent(seg.slug || String(seg.id))}`,
                    searchParams.verified_only,
                  );
                  return (
                    <Fragment key={`${seg.id}-${seg.slug}-${idx}`}>
                      <span className="text-neutral-400 select-none dark:text-neutral-500" aria-hidden>
                        /
                      </span>
                      {isLast ? (
                        <span
                          className="text-neutral-500 dark:text-neutral-400 font-normal truncate max-w-[min(55vw,480px)]"
                          title={seg.name}
                        >
                          {seg.name}
                        </span>
                      ) : (
                        <Link
                          href={href}
                          className="text-brand-green font-medium hover:underline truncate max-w-[min(42vw,220px)] dark:text-brand-green"
                        >
                          {seg.name}
                        </Link>
                      )}
                    </Fragment>
                  );
                })
              : activeCategory && (
                  <>
                    <span className="text-neutral-400 select-none dark:text-neutral-500" aria-hidden>
                      /
                    </span>
                    <span
                      className="text-neutral-500 dark:text-neutral-400 font-normal truncate max-w-[min(55vw,480px)]"
                      title={activeCategory.name}
                    >
                      {activeCategory.name}
                    </span>
                  </>
                )}
          </nav>
        )}
      </div>

      <div className="grid gap-6 md:gap-8 lg:grid-cols-4">
        {/* Desktop filter sidebar – match React */}
        <aside className="hidden lg:block lg:col-span-1 w-full h-0 min-h-full">
          <div className="sticky z-20 w-full" style={{ top: 'calc(var(--site-header-height, 9rem) + 0.75rem)' }}>
          <FilterPanel
            filters={filters}
            categories={categories}
            categoryTypeOptions={categoryTypeOptions}
            categoryTypeLabel={categoryTypeLabel}
            isVehicleCategory={isVehicleCategory}
            className="border border-neutral-200 dark:border-neutral-700 shadow-sm"
          />
          </div>
        </aside>

        {/* Mobile filter button – match React */}
        <div className="lg:hidden mb-4 lg:col-span-3">
          <button
            type="button"
            onClick={() => setIsFilterDrawerOpen(true)}
            className="w-full flex items-center justify-center gap-2 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 px-4 py-3 rounded-xl font-semibold hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors"
          >
            <Filter size={18} aria-hidden />
            Filters
            {activeFilterCount > 0 && (
              <span className="bg-brand-orange text-white text-xs px-2 py-0.5 rounded-full">{activeFilterCount}</span>
            )}
          </button>
        </div>

        {/* Mobile filter drawer – match React */}
        {isFilterDrawerOpen && (
          <div className="lg:hidden fixed inset-0 z-50 bg-black/50 flex items-end" aria-modal="true">
            <div className="bg-white dark:bg-neutral-800 w-full h-[90vh] rounded-t-3xl overflow-hidden flex flex-col">
              <div className="p-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
                <h3 className="text-lg font-bold text-brand-dark dark:text-neutral-100">Filters</h3>
                <button
                  type="button"
                  onClick={() => setIsFilterDrawerOpen(false)}
                  className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                  aria-label="Close filters"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
                <FilterPanel
                  filters={filters}
                  categories={categories}
                  categoryTypeOptions={categoryTypeOptions}
                  categoryTypeLabel={categoryTypeLabel}
                  isVehicleCategory={isVehicleCategory}
                  onClose={() => setIsFilterDrawerOpen(false)}
                  className="flex-1 border-0 rounded-none shadow-none"
                />
              </div>
            </div>
          </div>
        )}

        {/* Main content – match React grid */}
        <div className="lg:col-span-3">
          <div className="mb-6">
            <ThirdPartyAd placement={topAdPlacement} categoryId={adCategoryId} />
          </div>

          <div className="flex items-center w-full gap-3 mb-4 md:mb-5">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <LayoutGrid size={20} className="text-brand-dark dark:text-neutral-100" aria-hidden />
              {activeCategory && (
                <span className="text-lg md:text-xl" aria-hidden>
                  {getCategoryIcon(activeCategory.id, activeCategory.name, activeCategory.icon ?? undefined)}
                </span>
              )}
              <h1 className="text-lg md:text-xl font-black text-brand-dark dark:text-neutral-100 tracking-tight">
                {headingText}
              </h1>
            </div>
            {totalListings > 0 && (
              <div className="shrink-0 ml-auto">
                <span className="text-sm text-neutral-500 dark:text-neutral-400 font-bold whitespace-nowrap">
                  ({totalListings} listings)
                </span>
              </div>
            )}
          </div>
          {combinedItems.length > 0 ? (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-6">
                {combinedItems.map((item, index) => {
                  const card =
                    item._type === 'ad' ? (
                      <ClassifiedAdCard
                        key={`ad-${(item as ClassifiedAd & { _type: 'ad' }).id}`}
                        ad={item as ClassifiedAd & { _type: 'ad' }}
                        hideBadge={true}
                      />
                    ) : (
                      <ProductCard
                        key={`product-${(item as import('@/lib/api').ProductSummary & { _type: 'product' }).id}`}
                        product={item as import('@/lib/api').ProductSummary & { _type: 'product' }}
                        priority={index < 4}
                      />
                    );

                  const shouldInsertMiddleAd = index === 7 && combinedItems.length >= 8;
                  if (!shouldInsertMiddleAd) return card;

                  return (
                    <Fragment key={`with-ad-${index}`}>
                      {card}
                      <div className="col-span-2 lg:col-span-3 xl:col-span-4">
                        <ThirdPartyAd placement="category_middle" categoryId={adCategoryId} />
                      </div>
                    </Fragment>
                  );
                })}
              </div>
              {lastPage > 1 && (
                <div className="mt-8 mb-12 flex items-center justify-center gap-2">
                  {currentPage > 1 && (
                    <Link
                      href={buildSearchUrl(currentPage - 1)}
                      className="px-4 py-2 rounded-lg text-sm font-medium bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors"
                    >
                      Previous
                    </Link>
                  )}
                  <span className="text-sm text-neutral-500 dark:text-neutral-400">
                    Page {currentPage} of {lastPage}
                  </span>
                  {currentPage < lastPage && (
                    <Link
                      href={buildSearchUrl(currentPage + 1)}
                      className="px-4 py-2 rounded-lg text-sm font-medium bg-brand-orange text-white hover:bg-brand-orange-hover transition-colors"
                    >
                      Next
                    </Link>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="empty-state-card text-center py-12 bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 border-dashed">
              <LayoutGrid size={48} className="mx-auto text-neutral-300 dark:text-neutral-600 mb-4" aria-hidden />
              <h3 className="text-lg font-bold text-brand-dark dark:text-neutral-100 mb-2">No listings found</h3>
              <p className="text-neutral-500 dark:text-neutral-400 mb-6">
                There are no products or ads available at the moment.
              </p>
              <Link
                href="/"
                className="inline-flex items-center gap-2 bg-brand-orange text-white px-6 py-3 rounded-xl font-bold hover:bg-brand-orange-hover transition-colors"
              >
                Back to Home
              </Link>
            </div>
          )}

          {/* SEO content section (per-category) — see src/lib/categorySeo.ts */}
          {listingSeo?.h2 && (
            <section className="mt-10 border-t border-neutral-200 dark:border-neutral-700 pt-6">
              <h2 className="text-lg md:text-xl font-bold text-brand-dark dark:text-neutral-100 mb-3">
                {listingSeo.h2}
              </h2>
              {listingSeo.seoBody && (
                <p className="text-sm md:text-base text-neutral-600 dark:text-neutral-400 leading-relaxed max-w-3xl">
                  {listingSeo.seoBody}
                </p>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
